import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type ReactElement,
  type Ref,
  type RefCallback,
} from 'react';
import { flushSync } from 'react-dom';
import {
  FieldError,
  Input,
  Label,
  Text,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { announcePolite } from './announce';
import { commitWhileInvalid } from './TextField';
import s from './fields.module.css';

/**
 * APP-249 (the Spot redesign's change 9, Tom's go 2026-10-31): cash formats as it's typed,
 * "$1,000.00", here and, as each area is converted, in every cash box in the app.
 *
 * Only the box formats. The text it hands back is the amount as typed without the box's own "$"
 * and commas ("1000", "1000.5"), the same text a plain box gave, so the caller's check, its
 * messages and the cents it sends are unchanged. Anything that isn't a plain amount ("12a", "-5")
 * is handed back as typed, for that check to refuse as before; nothing typed is ever dropped.
 *
 * D5 (app-builder, 2026-11-01): a comma the person types or pastes counts as a thousands
 * separator only where it groups correctly ("1,000", "12,500", "1,000,000"). Anywhere else
 * ("25,00", "1,00", ",500") the text is handed back as typed and refused, never read as an amount
 * 100 times the one meant. The commas the box drew itself are its own, and move as digits go in.
 *
 * APP-253 (change 9, every other cash box): the formatting is `useDollarText`, so a box keeps its
 * own markup, and `useDollarCents` puts it in front of a caller that holds cents, as the number
 * boxes it replaces did: the same keystrokes refused, and the amount handed over, held within the
 * box's limits, only when it's entered (on leaving the box or pressing Enter).
 */

/** A plain amount: digits, then at most one point and the digits after it. */
const PLAIN = /^\d*\.?\d*$/;
/** An amount as written: whole dollars, perhaps with commas, then perhaps a point and places. */
const WRITTEN = /^([\d,]*)(?:\.(\d*))?$/;
/** Whole dollars whose commas group thousands: every group after the first exactly 3 digits. */
const GROUPED = /^[1-9]\d{0,2}(?:,\d{3})+$/;

/**
 * What was typed, read as an amount: "$1,000.5" is "1000.5". Anything else stays as typed,
 * including a comma that doesn't group thousands (D5).
 */
export function dollarValue(text: string): string {
  const match = WRITTEN.exec(text.trim().replace(/^\$/, ''));
  if (match === null) return text;
  const [, whole = '', places] = match;
  if (whole.includes(',') && !GROUPED.test(whole)) return text;
  // "007" reads as 7, as the amount itself does; a lone 0 before the point stays.
  const digits = whole.replaceAll(',', '').replace(/^0+(?=\d)/, '');
  return places === undefined ? digits : `${digits}.${places}`;
}

/**
 * The text with the box's own commas taken out: those carried over unchanged from what it showed
 * (`before`), around what was just typed or pasted, whose commas are the person's. `caret` moves
 * back by each comma taken out before it. APP-253: so are the last `padding` characters of
 * `before`, the places it added to show the amount ("$123.00" for "123"), when they're carried.
 */
function ownCommasOut(
  before: string,
  after: string,
  caret: number,
  padding = 0,
): { text: string; caret: number } {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start])
    start += 1;
  let end = 0;
  while (
    end < before.length - start &&
    end < after.length - start &&
    before[before.length - 1 - end] === after[after.length - 1 - end]
  )
    end += 1;
  let text = '';
  let at = caret;
  const padded = padding > 0 && end >= padding;
  for (let i = 0; i < after.length; i += 1) {
    const carried = i < start || i >= after.length - end;
    if ((carried && after[i] === ',') || (padded && i >= after.length - padding)) {
      if (i < caret) at -= 1;
    } else text += after[i] ?? '';
  }
  return { text, caret: at };
}

/**
 * An amount as the box shows it. While typing: grouped, with the places typed so far ("$1,000",
 * "$1,000.5"). Once the box is left: two places ("$1,000.00"), but only for an amount a caller's
 * check takes (whole dollars, then none, one or two places); anything else shows as typed, so a
 * message about it reads true. A whole-dollars box shows whole dollars as they are ("$1,000").
 */
export function dollarDisplay(value: string, typing: boolean, wholeDollars = false): string {
  if (value === '' || !PLAIN.test(value)) return value;
  const [whole = '', places] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const asTyped = `$${grouped}${places === undefined ? '' : `.${places}`}`;
  if (typing || whole === '' || (places !== undefined && !/^\d{1,2}$/.test(places))) return asTyped;
  if (wholeDollars && places === undefined) return asTyped;
  return `$${grouped}.${(places ?? '').padEnd(2, '0')}`;
}

/** How many digits and points come before `end` in `text`: the caret's place, whatever the commas. */
const placeOf = (text: string, end: number): number =>
  text.slice(0, end).replace(/[^\d.]/g, '').length;

/** Whether `next` is `value` with one run of it deleted, and nothing put in its place. */
function deletedFrom(value: string, next: string): boolean {
  if (next.length >= value.length) return false;
  let same = 0;
  while (same < next.length && next[same] === value[same]) same += 1;
  return value.endsWith(next.slice(same));
}

/** Where the caret goes in `text` to have `place` digits and points before it. */
function caretAt(text: string, place: number): number {
  if (place <= 0) return text.startsWith('$') ? 1 : 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (/[\d.]/.test(text[i] ?? '')) seen += 1;
    if (seen === place) return i + 1;
  }
  return text.length;
}

/**
 * The box's text if an edit goes ahead, worked out the way React Aria's number box does it, or
 * null when the edit can't be read ahead of time (that edit is then checked once it's in).
 */
function textAfter(box: HTMLInputElement, event: InputEvent): string | null {
  const { value } = box;
  const start = box.selectionStart ?? value.length;
  const end = box.selectionEnd ?? start;
  switch (event.inputType) {
    case 'deleteContentBackward':
      return start === end
        ? value.slice(0, Math.max(0, start - 1)) + value.slice(end)
        : value.slice(0, start) + value.slice(end);
    case 'deleteContentForward':
      return start === end
        ? value.slice(0, start) + value.slice(end + 1)
        : value.slice(0, start) + value.slice(end);
    case 'deleteContent':
    case 'deleteByCut':
    case 'deleteByDrag':
      return value.slice(0, start) + value.slice(end);
    default: {
      const data = event.data ?? event.dataTransfer?.getData('text/plain') ?? null;
      return data === null ? null : value.slice(0, start) + data + value.slice(end);
    }
  }
}

export interface DollarTextOptions {
  /** The amount as the caller holds it: plain text such as "1000" or "1000.00", or "" for none. */
  value: string;
  /** Each keystroke's amount, without the box's own "$" and commas (`dollarValue`). */
  onChange: (value: string) => void;
  /** Whole dollars ("$1,000" once left), with the number keypad on phones. */
  wholeDollars?: boolean;
  /**
   * The amounts the box takes. An edit that would leave any other doesn't go in, as a number box
   * refused it: the caller's check never sees it. Without it, everything typed is handed back.
   * Deleting from a plain amount (one run taken out, nothing put in) always goes in, so an amount
   * the box wouldn't take (cents held in a whole-dollar box) can still be deleted.
   */
  accepts?: (value: string) => boolean;
  onFocusChange?: ((focused: boolean) => void) | undefined;
  /** The caller's own ref to the input, if it needs one (to move focus to it). */
  inputRef?: Ref<HTMLInputElement> | undefined;
}

export interface DollarText {
  /** For the React Aria TextField. */
  field: Pick<AriaTextFieldProps, 'value' | 'inputMode' | 'onChange' | 'onFocusChange'> & {
    value: string;
  };
  /** For the Input inside it. */
  inputRef: RefCallback<HTMLInputElement>;
}

/** Points a caller's ref, of either kind, at the input. */
function pointRef(ref: Ref<HTMLInputElement> | undefined, box: HTMLInputElement | null): void {
  if (typeof ref === 'function') ref(box);
  else if (ref) ref.current = box;
}

/**
 * Dollars as they're typed, for a React Aria TextField with markup of its own: the text it shows,
 * the plain amount handed back for each keystroke, and the caret kept by the digit just typed.
 */
export function useDollarText({
  value,
  onChange,
  wholeDollars = false,
  accepts,
  onFocusChange,
  inputRef,
}: DollarTextOptions): DollarText {
  const input = useRef<HTMLInputElement>(null);
  const ref = useCallback(
    (box: HTMLInputElement | null) => {
      input.current = box;
      pointRef(inputRef, box);
    },
    [inputRef],
  );
  // APP-253: "typing" starts with the first edit, not on entering the box, so the text never
  // changes under a selection: Tab, a select-all or a fill, then typing, replaces "$500.00".
  const [typing, setTyping] = useState(false);
  // Where the caret belongs after the box is drawn again: by digits and points when the commas
  // moved, else at the same place in the text as typed.
  const caret = useRef<{ place: number } | { index: number } | null>(null);
  const shown = dollarDisplay(value, typing, wholeDollars);
  useLayoutEffect(() => {
    const wanted = caret.current;
    caret.current = null;
    const box = input.current;
    if (wanted === null || box === null || box.ownerDocument.activeElement !== box) return;
    const at =
      'place' in wanted
        ? caretAt(box.value, wanted.place)
        : Math.min(wanted.index, box.value.length);
    if (box.selectionStart !== at || box.selectionEnd !== at) box.setSelectionRange(at, at);
  });
  // What the box holds after an edit: while it shows a plain amount, every comma it shows is its
  // own; a comma typed or pasted now is the person's, and `dollarValue` reads it by D5's rule.
  const padding = shown.length - dollarDisplay(value, true, wholeDollars).length;
  const read = (typed: string, end: number) => {
    const own = PLAIN.test(value)
      ? ownCommasOut(shown, typed, end, padding)
      : { text: typed, caret: end };
    return { own, next: dollarValue(own.text) };
  };
  const refused = (next: string): boolean =>
    accepts !== undefined &&
    !accepts(next) &&
    !(PLAIN.test(next) && PLAIN.test(value) && deletedFrom(value, next));
  // APP-253: a refused edit is stopped before it reaches the box, so the text and the caret stay.
  const refuse = useEffectEvent((event: InputEvent) => {
    const box = input.current;
    const after = box === null ? null : textAfter(box, event);
    if (after !== null && refused(read(after, after.length).next)) event.preventDefault();
  });
  const refusing = accepts !== undefined;
  useEffect(() => {
    const box = input.current;
    if (!refusing || box === null) return;
    const listener = (event: InputEvent) => {
      refuse(event);
    };
    box.addEventListener('beforeinput', listener);
    return () => {
      box.removeEventListener('beforeinput', listener);
    };
  }, [refusing]);
  return {
    inputRef: ref,
    field: {
      value: shown,
      inputMode: wholeDollars ? 'numeric' : 'decimal',
      onFocusChange: (focused) => {
        if (!focused) setTyping(false);
        onFocusChange?.(focused);
      },
      onChange: (typed) => {
        const box = input.current;
        const end = box?.selectionStart ?? typed.length;
        const { own, next } = read(typed, end);
        // An edit the check above couldn't read ahead (a composition, say): the box goes back.
        if (refused(next)) return;
        const drawn = dollarDisplay(next, true, wholeDollars);
        // Drawn differently from what's in the box: keep the caret by what was just typed.
        if (box !== null && drawn !== typed)
          caret.current =
            drawn === own.text ? { index: own.caret } : { place: placeOf(own.text, own.caret) };
        setTyping(true);
        onChange(next);
      },
    },
  };
}

/** Whole dollars, or a point and zeros only ("$1,500.00" pasted): no cents, as the box took. */
const WHOLE_AMOUNT = /^\d{0,13}(?:\.0*)?$/;
/** Dollars and up to two places: a cent is the smallest amount the app keeps (APP-010). */
const CENTS_AMOUNT = /^\d{0,13}(?:\.\d{0,2})?$/;
const acceptsWhole = (amount: string): boolean => WHOLE_AMOUNT.test(amount);
const acceptsCents = (amount: string): boolean => CENTS_AMOUNT.test(amount);

/** An accepted amount in cents: null when empty, undefined when there's no digit (".") to read. */
export function amountToCents(amount: string): number | null | undefined {
  if (amount === '') return null;
  const [whole = '', places = ''] = amount.split('.');
  if (whole === '' && places === '') return undefined;
  return Number(BigInt(whole || '0') * 100n + BigInt(places.slice(0, 2).padEnd(2, '0')));
}

/** Cents as the box's text: "1000" for whole dollars, "1000.50" otherwise, "" for none. */
export function centsToAmount(cents: number | null): string {
  if (cents === null) return '';
  // The sign is written once, so -1 to -99 cents keep it (Math.trunc(-0.5) is -0).
  const sign = cents < 0 ? '-' : '';
  const whole = Math.trunc(Math.abs(cents) / 100);
  const rest = Math.abs(cents) % 100;
  return rest === 0
    ? `${sign}${String(whole)}`
    : `${sign}${String(whole)}.${String(rest).padStart(2, '0')}`;
}

export interface DollarCentsOptions {
  /** Money is integer cents everywhere (APP-010). `null` is empty. */
  valueCents: number | null;
  /**
   * The amount once it's entered, on leaving the box or pressing Enter, held within `minCents`
   * and `maxCents`, and only when it differs from `valueCents`: as the number box gave it.
   */
  onChangeCents?: ((cents: number | null) => void) | undefined;
  minCents?: number | undefined;
  maxCents?: number | undefined;
  /** Whole dollars only (most award amounts): "." and cents don't go in. */
  wholeDollars?: boolean | undefined;
  onFocusChange?: ((focused: boolean) => void) | undefined;
  /** The caller's own ref to the input, if it needs one (to move focus to it). */
  inputRef?: Ref<HTMLInputElement> | undefined;
}

export type DollarCents = DollarText & {
  field: DollarText['field'] & Pick<AriaTextFieldProps, 'onKeyDown'>;
};

/**
 * APP-253: a dollar box in front of a caller that holds cents, in place of a React Aria
 * NumberField. The text being typed stays in the box; the caller hears of it only when it's
 * entered, as before, so its checks, range messages and payloads are unchanged.
 */
export function useDollarCents({
  valueCents,
  onChangeCents,
  minCents = 0,
  maxCents,
  wholeDollars = true,
  onFocusChange,
  inputRef,
}: DollarCentsOptions): DollarCents {
  // What's being typed, until it's entered; null shows what the caller holds.
  const [draft, setDraft] = useState<string | null>(null);
  // The caller's amount changed while typing (another box, a fresh read): its amount wins, as the
  // number box's did.
  const [held, setHeld] = useState(valueCents);
  if (!Object.is(held, valueCents)) {
    setHeld(valueCents);
    setDraft(null);
  }
  const enter = () => {
    if (draft === null) return;
    setDraft(null);
    const typed = amountToCents(draft);
    // A lone "." goes back to the amount held, as the number box did.
    if (typed === undefined) return;
    // Cents left in a whole-dollar box (deleted down from cents it held) round to the dollar, as
    // the number box rounded to its step.
    const cents = typed !== null && wholeDollars ? Math.round(typed / 100) * 100 : typed;
    const next =
      cents === null
        ? null
        : Math.max(minCents, maxCents === undefined ? cents : Math.min(cents, maxCents));
    // Held within the limits: said, as the number box said the amount it put in the box.
    if (cents !== null && next !== null && next !== cents)
      announcePolite(
        `Changed to ${dollarDisplay(centsToAmount(next), false, wholeDollars)}, the ${next < cents ? 'most' : 'least'} it takes.`,
      );
    if (next !== valueCents) onChangeCents?.(next);
  };
  const text = useDollarText({
    value: draft ?? centsToAmount(valueCents),
    onChange: setDraft,
    wholeDollars,
    accepts: wholeDollars ? acceptsWhole : acceptsCents,
    onFocusChange: (focused) => {
      if (!focused) enter();
      onFocusChange?.(focused);
    },
    inputRef,
  });
  return {
    ...text,
    field: {
      ...text.field,
      onKeyDown: (event) => {
        // Enter enters the amount before a form's submit reads it, as the number box did.
        if (event.key === 'Enter') flushSync(enter);
        event.continuePropagation();
      },
    },
  };
}

export interface DollarFieldProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'value' | 'defaultValue' | 'onChange' | 'inputMode' | 'type'
> {
  /** Visible label (every input has one). */
  label: string;
  /** The amount as the caller holds it: plain text such as "1000" or "1000.00", or "" for none. */
  value: string;
  /** Each keystroke's amount, without the box's own "$" and commas (`dollarValue`). */
  onChange: (value: string) => void;
  /** Help text under the input. */
  description?: string;
  /** Message shown when the field is invalid. */
  errorMessage?: string;
  placeholder?: string;
  /** Keep the label for screen readers only, where a column heading already names the input. */
  labelHidden?: boolean;
}

/** A labelled dollar input that formats as it's typed and hands back the plain amount. */
export function DollarField({
  label,
  value,
  onChange,
  description,
  errorMessage,
  placeholder,
  isRequired,
  labelHidden = false,
  onFocusChange,
  ...rest
}: DollarFieldProps): ReactElement {
  const { field, inputRef } = useDollarText({ value, onChange, onFocusChange });
  return (
    <AriaTextField {...rest} {...field} isRequired={isRequired ?? false} className={s.field}>
      <Label className={labelHidden ? 'visually-hidden' : s.label}>
        {label}
        {isRequired === true && (
          <span className={s.required} aria-hidden="true">
            *
          </span>
        )}
      </Label>
      <div className={s.body}>
        <Input
          ref={inputRef}
          className={s.input}
          {...(placeholder !== undefined && { placeholder })}
          {...(rest.isInvalid === undefined && { onInput: commitWhileInvalid })}
        />
        {description !== undefined && (
          <Text slot="description" className={s.description}>
            {description}
          </Text>
        )}
        <FieldError className={s.error}>{errorMessage}</FieldError>
      </div>
    </AriaTextField>
  );
}
